import React, { useState } from 'react';
import {
  Box, Button, Checkbox, Divider, InputAdornment, ListItemText, MenuItem, Popover, Stack,
  TextField, Typography,
} from '@mui/material';
import {
  ViewWeekOutlined as WeeksIcon,
  PersonOutlined as DriverIcon,
  FactCheckOutlined as StatusIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  ChoiceFilter, DateRange, DateRangeFilter, FilterBar, FilterButton,
} from '../../components/ui/FilterBar';
import { shortDate } from './payrollUI';

export { FilterButton };
export type { DateRange };

// The reports' filter bar: one row under the title, the way analytics
// tools lay it out. A date range first (presets, or a custom range applied
// with a button), then one drop-down per dimension. Each button names its
// current value and turns blue when it narrows anything, so the row reads
// as a sentence of what is on screen. Nothing appears or disappears: the
// row is the same five controls every time.
//
// Controlled: the page owns the state and decides what each filter means.

export type ReportStatus = 'all' | 'finalized' | 'draft';
export interface FilterWeek { weekStart: string; weekEnd: string; status: 'draft' | 'finalized' }

const DAY = 86400000;
const day = (iso: string) => iso.slice(0, 10);
const addDays = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * DAY).toISOString().slice(0, 10);

/**
 * The presets, anchored on the newest uploaded week rather than today:
 * a statement arrives days after its week, so "last 8 weeks" means the
 * last 8 there are numbers for.
 */
export function presets(all: FilterWeek[]): { key: string; label: string; range: DateRange }[] {
  if (!all.length) return [];
  const end = day(all[all.length - 1].weekEnd);
  const weeks = (k: number) => ({ from: addDays(end, -(k * 7) + 1), to: end });
  return [
    { key: '4w', label: 'Last 4 weeks', range: weeks(4) },
    { key: '8w', label: 'Last 8 weeks', range: weeks(8) },
    { key: '13w', label: 'Last 13 weeks', range: weeks(13) },
    { key: '26w', label: 'Last 26 weeks', range: weeks(26) },
    { key: '52w', label: 'Last 52 weeks', range: weeks(52) },
    { key: 'ytd', label: 'Year to date', range: { from: `${end.slice(0, 4)}-01-01`, to: end } },
    { key: 'all', label: 'All time', range: { from: day(all[0].weekStart), to: end } },
  ];
}
export const DEFAULT_PRESET = '8w';

const ReportFilters: React.FC<{
  /** Every uploaded week, oldest first. */
  all: FilterWeek[];
  range: DateRange | null;
  onRange: (r: DateRange) => void;
  /** The weeks inside the range, for picking single ones. */
  inRange: FilterWeek[];
  picked: Set<string>;
  onPicked: (next: Set<string>) => void;
  people: { accountId: string; name: string }[];
  drivers: string[];
  onDrivers: (ids: string[]) => void;
  status: ReportStatus;
  onStatus: (s: ReportStatus) => void;
  onReset: () => void;
  canReset: boolean;
}> = ({
  all, range, onRange, inRange, picked, onPicked, people, drivers, onDrivers, status, onStatus, onReset, canReset,
}) => {
  const none = all.length === 0;
  const list = presets(all);
  const weeksIn = (r: DateRange) => all.filter((w) => day(w.weekEnd) >= r.from && day(w.weekStart) <= r.to).length;

  const [weeksAnchor, setWeeksAnchor] = useState<HTMLElement | null>(null);
  const [driverAnchor, setDriverAnchor] = useState<HTMLElement | null>(null);
  const [driverQuery, setDriverQuery] = useState('');

  const toggle = <T,>(set: Set<T>, v: T) => { const n = new Set(set); if (n.has(v)) n.delete(v); else n.add(v); return n; };
  const chosen = new Set(drivers);
  const shownPeople = people.filter((p) => p.name.toLowerCase().includes(driverQuery.trim().toLowerCase()));
  const counts = {
    all: inRange.length,
    finalized: inRange.filter((w) => w.status === 'finalized').length,
    draft: inRange.filter((w) => w.status === 'draft').length,
  };
  const weeksValue = picked.size === 0 ? 'All' : picked.size === 1 ? `Ending ${shortDate(Array.from(picked)[0])}` : `${picked.size} weeks`;
  const driversValue = drivers.length === 0 ? 'All' : drivers.length === 1 ? (people.find((p) => p.accountId === drivers[0])?.name || '1 driver') : `${drivers.length} drivers`;

  return (
    <FilterBar onReset={onReset} canReset={canReset}>
      <DateRangeFilter
        range={range}
        presets={list}
        onChange={onRange}
        defaultKey={DEFAULT_PRESET}
        disabled={none}
        describe={(r) => { const n = weeksIn(r); return `${n} ${n === 1 ? 'week' : 'weeks'} uploaded in this range`; }}
      />
      <FilterButton icon={<WeeksIcon />} label="Weeks" value={weeksValue} active={picked.size > 0} disabled={!inRange.length} onClick={(e) => setWeeksAnchor(e.currentTarget)} />
      <FilterButton icon={<DriverIcon />} label="Drivers" value={driversValue} active={drivers.length > 0} disabled={!people.length} onClick={(e) => setDriverAnchor(e.currentTarget)} />
      <ChoiceFilter
        icon={<StatusIcon />}
        label="Status"
        value={status}
        defaultValue="all"
        onChange={onStatus}
        disabled={!inRange.length}
        choices={[
          { key: 'all', label: 'All weeks', short: 'All', note: counts.all },
          { key: 'finalized', label: 'Finalized only', short: 'Finalized', note: counts.finalized },
          { key: 'draft', label: 'Drafts only', short: 'Draft', note: counts.draft },
        ]}
      />

      {/* Single weeks */}
      <Popover open={!!weeksAnchor} anchorEl={weeksAnchor} onClose={() => setWeeksAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} slotProps={{ paper: { sx: { mt: 0.5, width: 260 } } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography variant="subtitle2">Weeks in the range</Typography>
          <Button size="small" disabled={!picked.size} onClick={() => onPicked(new Set())} sx={{ minWidth: 0, p: 0 }}>Clear</Button>
        </Stack>
        <Box sx={{ maxHeight: 320, overflow: 'auto', p: 0.75 }}>
          {[...inRange].reverse().map((w) => (
            <MenuItem key={w.weekEnd} dense onClick={() => onPicked(toggle(picked, w.weekEnd))} sx={{ borderRadius: 1 }}>
              <Checkbox size="small" checked={picked.has(w.weekEnd)} sx={{ p: 0.5, mr: 1 }} inputProps={{ 'aria-label': `Week ending ${shortDate(w.weekEnd)}` }} />
              <ListItemText primary={`${shortDate(w.weekStart)} – ${shortDate(w.weekEnd)}`} />
              {w.status === 'draft' && <Typography variant="caption" color="text.secondary">draft</Typography>}
            </MenuItem>
          ))}
        </Box>
      </Popover>

      {/* Drivers */}
      <Popover open={!!driverAnchor} anchorEl={driverAnchor} onClose={() => { setDriverAnchor(null); setDriverQuery(''); }} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} slotProps={{ paper: { sx: { mt: 0.5, width: 300 } } }}>
        <Box sx={{ p: 1.5, pb: 1 }}>
          <TextField
            autoFocus size="small" fullWidth placeholder="Search drivers" value={driverQuery} onChange={(e) => setDriverQuery(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            inputProps={{ 'aria-label': 'Search drivers' }}
          />
        </Box>
        <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
          <Typography variant="caption" color="text.secondary">{drivers.length ? `${drivers.length} of ${people.length} selected` : `All ${people.length} drivers`}</Typography>
          <Button size="small" disabled={!drivers.length} onClick={() => onDrivers([])} sx={{ minWidth: 0, p: 0 }}>Clear</Button>
        </Stack>
        <Box sx={{ maxHeight: 320, overflow: 'auto', p: 0.75 }}>
          {shownPeople.map((p) => (
            <MenuItem key={p.accountId} dense onClick={() => onDrivers(Array.from(toggle(chosen, p.accountId)))} sx={{ borderRadius: 1 }}>
              <Checkbox size="small" checked={chosen.has(p.accountId)} sx={{ p: 0.5, mr: 1 }} inputProps={{ 'aria-label': p.name }} />
              <ListItemText primary={p.name} />
            </MenuItem>
          ))}
          {!shownPeople.length && <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>Nobody by that name</Typography>}
        </Box>
        <Divider />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, py: 1.25 }}>
          With drivers picked, revenue is the statement&apos;s shared out by their stops, and payroll tax by their pay.
        </Typography>
      </Popover>

    </FilterBar>
  );
};

export default ReportFilters;
